import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Pin, PinOff, Reply, Edit3, Trash2, EyeOff, Clock, 
  CheckCircle, User, Badge, MoreVertical 
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import { Badge as UIBadge } from '../ui/Badge';
import OwnerReplyModal from './OwnerReplyModal';
import questionService from '../../services/questionService';
import toast from 'react-hot-toast';

const QuestionCard = ({ question, projectSlug, isProjectOwner, currentUser, isPinned }) => {
  const queryClient = useQueryClient();
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [showActions, setShowActions] = useState(false);

  // Check if current user is the question author
  const isQuestionAuthor = currentUser?.id === question.user_id;

  // Mutations
  const togglePinMutation = useMutation({
    mutationFn: () => questionService.togglePinQuestion(question.id),
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['project-questions', projectSlug]);
      queryClient.invalidateQueries(['question-stats', projectSlug]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update pin status');
    }
  });

  const hideQuestionMutation = useMutation({
    mutationFn: () => questionService.hideQuestion(question.id),
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['project-questions', projectSlug]);
      queryClient.invalidateQueries(['question-stats', projectSlug]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to hide question');
    }
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: () => questionService.deleteQuestion(question.id),
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['project-questions', projectSlug]);
      queryClient.invalidateQueries(['question-stats', projectSlug]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete question');
    }
  });

  const deleteAnswerMutation = useMutation({
    mutationFn: () => questionService.deleteAnswer(question.id),
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries(['project-questions', projectSlug]);
      queryClient.invalidateQueries(['question-stats', projectSlug]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete answer');
    }
  });

  const handleTogglePin = () => {
    togglePinMutation.mutate();
  };

  const handleHideQuestion = () => {
    if (window.confirm('Are you sure you want to hide this question? This action cannot be undone.')) {
      hideQuestionMutation.mutate();
    }
  };

  const handleDeleteQuestion = () => {
    if (window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      deleteQuestionMutation.mutate();
    }
  };

  const handleDeleteAnswer = () => {
    if (window.confirm('Are you sure you want to delete this answer? This action cannot be undone.')) {
      deleteAnswerMutation.mutate();
    }
  };

  const handleReplySuccess = () => {
    setShowReplyModal(false);
    queryClient.invalidateQueries(['project-questions', projectSlug]);
    queryClient.invalidateQueries(['question-stats', projectSlug]);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getUserDisplayName = (user) => {
    return user.full_name || user.pi_username || 'Anonymous';
  };

  const getUserBadgeColor = (badgeLevel) => {
    switch (badgeLevel) {
      case 'bronze': return 'bg-amber-100 text-amber-800';
      case 'silver': return 'bg-gray-100 text-gray-800';
      case 'gold': return 'bg-yellow-100 text-yellow-800';
      case 'platinum': return 'bg-purple-100 text-purple-800';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className={`border-0 shadow-sm hover:shadow-md transition-all duration-300 ${
        isPinned ? 'bg-gradient-to-r from-primary-teal/5 to-transparent border-l-4 border-l-primary-teal' : ''
      }`}>
        <CardContent className="p-0">
          {/* Question Section */}
          <div className="p-4 border-b border-neutral-100">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                {/* Question Header */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {isPinned && (
                    <UIBadge variant="secondary" className="bg-primary-teal/10 text-primary-teal text-xs">
                      📌 Pinned
                    </UIBadge>
                  )}
                  {question.is_answered ? (
                    <UIBadge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Answered
                    </UIBadge>
                  ) : (
                    <UIBadge variant="secondary" className="bg-orange-100 text-orange-800 text-xs">
                      <Clock className="w-3 h-3 mr-1" />
                      Awaiting Response
                    </UIBadge>
                  )}
                </div>

                {/* Question Text */}
                <p className="text-neutral-900 font-medium mb-3 leading-relaxed text-sm sm:text-base">
                  <span className="font-semibold text-primary-teal">Q:</span> {question.question}
                </p>

                {/* Question Meta */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-xs sm:text-sm text-neutral-600">
                  <div className="flex items-center gap-2">
                    {question.user.profile_image ? (
                      <img 
                        src={question.user.profile_image}
                        alt={getUserDisplayName(question.user)}
                        className="w-4 h-4 sm:w-5 sm:h-5 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-4 h-4 sm:w-5 sm:h-5 bg-gradient-to-br from-primary-teal to-primary-teal/80 rounded-full flex items-center justify-center">
                        <User className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                      </div>
                    )}
                    <span className="font-medium truncate max-w-[120px] sm:max-w-none">{getUserDisplayName(question.user)}</span>
                    {question.user.badge_level && (
                      <UIBadge className={`text-xs px-1.5 py-0.5 ${getUserBadgeColor(question.user.badge_level)}`}>
                        <Badge className="w-2 h-2 sm:w-3 sm:h-3 mr-1" />
                        <span className="hidden sm:inline">{question.user.badge_level}</span>
                        <span className="sm:hidden">{question.user.badge_level.charAt(0).toUpperCase()}</span>
                      </UIBadge>
                    )}
                  </div>
                  <span className="hidden sm:inline">•</span>
                  <span className="text-xs">{formatTimeAgo(question.created_at)}</span>
                </div>
              </div>

              {/* Actions Menu */}
              {(isProjectOwner || isQuestionAuthor) && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowActions(!showActions)}
                    className="h-8 w-8 p-0"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>

                  <AnimatePresence>
                    {showActions && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.1 }}
                        className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-10 min-w-[160px]"
                        onMouseLeave={() => setShowActions(false)}
                      >
                        {isProjectOwner && (
                          <>
                            {!question.answer && (
                              <button
                                onClick={() => {
                                  setShowReplyModal(true);
                                  setShowActions(false);
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2"
                              >
                                <Reply className="w-4 h-4" />
                                Reply
                              </button>
                            )}
                            {question.answer && (
                              <>
                                <button
                                  onClick={() => {
                                    setShowReplyModal(true);
                                    setShowActions(false);
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2"
                                >
                                  <Edit3 className="w-4 h-4" />
                                  Edit Reply
                                </button>
                                <button
                                  onClick={handleTogglePin}
                                  disabled={togglePinMutation.isLoading}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center gap-2"
                                >
                                  {isPinned ? (
                                    <>
                                      <PinOff className="w-4 h-4" />
                                      Unpin
                                    </>
                                  ) : (
                                    <>
                                      <Pin className="w-4 h-4" />
                                      Pin
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={handleDeleteAnswer}
                                  disabled={deleteAnswerMutation.isLoading}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 text-secondary-red flex items-center gap-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete Reply
                                </button>
                              </>
                            )}
                            <button
                              onClick={handleHideQuestion}
                              disabled={hideQuestionMutation.isLoading}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 text-secondary-red flex items-center gap-2"
                            >
                              <EyeOff className="w-4 h-4" />
                              Hide Question
                            </button>
                          </>
                        )}
                        {isQuestionAuthor && !question.answer && (
                          <button
                            onClick={handleDeleteQuestion}
                            disabled={deleteQuestionMutation.isLoading}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 text-secondary-red flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Question
                          </button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>

          {/* Answer Section */}
          {question.answer ? (
            <div className="p-3 sm:p-4 bg-gradient-to-r from-primary-teal/5 to-transparent">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-neutral-900 leading-relaxed mb-3 text-sm sm:text-base">
                    <span className="font-semibold text-primary-teal">A:</span> {question.answer.answer}
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-xs sm:text-sm text-neutral-600">
                    <div className="flex items-center gap-2">
                      {question.answer.owner.profile_image ? (
                        <img 
                          src={question.answer.owner.profile_image}
                          alt={getUserDisplayName(question.answer.owner)}
                          className="w-4 h-4 sm:w-5 sm:h-5 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-4 h-4 sm:w-5 sm:h-5 bg-gradient-to-br from-primary-teal to-primary-teal/80 rounded-full flex items-center justify-center">
                          <User className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                        </div>
                      )}
                      <span className="font-medium truncate max-w-[120px] sm:max-w-none">{getUserDisplayName(question.answer.owner)}</span>
                      <UIBadge className="bg-primary-teal/10 text-primary-teal text-xs px-1.5 py-0.5">
                        <span className="hidden sm:inline">Project Owner</span>
                        <span className="sm:hidden">Owner</span>
                      </UIBadge>
                    </div>
                    <span className="hidden sm:inline">•</span>
                    <span className="text-xs">Answered {formatTimeAgo(question.answer.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-3 sm:p-4 bg-neutral-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 text-neutral-600">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs sm:text-sm italic">Awaiting response from project owner</span>
                </div>
                {isProjectOwner && (
                  <Button
                    onClick={() => setShowReplyModal(true)}
                    size="sm"
                    className="flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Reply className="w-4 h-4" />
                    <span>Reply</span>
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Owner Reply Modal */}
      {showReplyModal && (
        <OwnerReplyModal
          question={question}
          isOpen={showReplyModal}
          onClose={() => setShowReplyModal(false)}
          onSuccess={handleReplySuccess}
        />
      )}
    </motion.div>
  );
};

export default QuestionCard;
